import { Clear, Search } from "@mui/icons-material";
import { Autocomplete, Button, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { DataGridPro as DataGrid, GridColDef, GridRowIdGetter, GridValidRowModel, gridStringOrNumberComparator, useGridApiRef } from "@mui/x-data-grid-pro";
import { useDefaultValues } from "./defaultValues";
import { EnumItemDto } from "./enum";
import { dateFormatByLandCode, dateTimeFormatByLandCode, formatNumber, getResource } from "./localization";
import { ArrayElement, ifNull, useRxJsObservablesWatcher, useRxJsSub } from "./utils";
import { useAccountStore, UserTypeId } from "./account";
import { DateField, InputSizedButtonSx } from "./form";
import { defaultItemsPerPage, enumAllValue, getId, getListPropFilterName, getListPropFilterProps, ListProp, ListStore, UseListStore } from "./listStore";
import { StoreWrapper } from "./store";
import { useEffect, useMemo, useRef, useState } from "react";
import { Observable, Subject, asapScheduler, observeOn, of } from "rxjs";
import { dayjs, useConfig } from "./config";

export interface ListStoreGridConfig<T> {
    header?: React.JSXElementConstructor<any>;
    columnValueTransforms?: { [U in keyof T]?: (value: T[U] | undefined) => T[U] | undefined; };
    onDoubleClick?: (item: T) => void;
    onClick?: (item: T) => void;
    autoHeight?: boolean;
    style?: React.CSSProperties;
    readOnly?: boolean;
    onRowSelected?(id: any): void;
    instantSearch?: true;
    renderExternalFilters?: () => JSX.Element;
    clearExternalFilters?: () => void;
    onClear?(): void;
    getRowClass?(item: T): ("bold" | "greenRibbon" | "redRibbon" | "warningRibbon" | "strikeThrough" | undefined | false)[] | false | null | undefined;
    scrollToSelectedItem$?: Observable<void>;
    assumeHavingHorizontalScrollbar?: true;
    filtersMaxWidth?: "md" | "lg" | "xl";
    hideFooter?: boolean;
    rowHeight?: number;
}

export function ListStoreGrid<T>({ useStore, config: configOrNull, listProps }: { useStore: UseListStore<T>; config?: ListStoreGridConfig<T>; listProps: ReturnType<typeof getListProps<T>>; }) {
    const config = configOrNull || {};
    const store = useStore();
    const def = useDefaultValues().serverData;

    const { columns } = useMemo(() => {
        const dateFormat = dateFormatByLandCode[def.landCode];
        const dateTimeFormat = dateTimeFormatByLandCode[def.landCode];
        const formatBool = (v: boolean) => v ? getResource("Ui.Yes", "Yes") as string : getResource("Ui.No", "No") as string;
        function getValueFormatter(item: ArrayElement<typeof listProps>): (value: string | number | null | undefined) => string | undefined {
            let transform = (value: any) => value;
            const columnTransform = config?.columnValueTransforms && item.prop.column && config.columnValueTransforms[item.prop.column];
            if (columnTransform) {
                transform = columnTransform;
            }
            if (item.prop.type === "currency") {
                return p => p == null ? "" : formatNumber(transform(p), def.landCode, true);
            }
            if (item.prop.type === "number") {
                return p => p == null ? "" : formatNumber(transform(p), def.landCode, false);
            }
            if (item.prop.type === "date") {
                return p => dateFormat ? (p || "") === "" ? "" : dayjs.tz(transform(p)).format(dateFormat) : transform(p);
            }
            if (item.prop.type === "datetime") {
                return p => dateTimeFormat ? (p || "") === "" ? "" : dayjs.tz(transform(p)).format(dateTimeFormat) : transform(p);
            }
            if (item.prop.type === "bool") {
                return p => p == null ? "" : formatBool(transform(p));
            }
            const enumItems = item.enumItems;
            if (enumItems != null) {
                return p => enumItems.find(r => r.id === p)?.name;
            }
            return transform;
        }

        return {
            columns: listProps.filter(p => typeof p.prop.column === "string" || p.prop.columnKey != null)
                .map(p => {
                    const valueFormatter = getValueFormatter(p);
                    const gridColDef: GridColDef = {
                        field: (p.prop.column ?? p.prop.columnKey) as string, headerName: p.prop.label, width: p.prop.width,
                        sortable: (p.prop.sortable || p.prop.sortable == null) ? true : false,
                        valueFormatter: p => valueFormatter(p.value),
                        align: p.prop.type === "currency" || p.prop.type === "date" || p.prop.type === "number" || p.prop.type === "datetime" ? "right" : p.prop.align ?? "left",
                        flex: p.prop.flex,
                        renderCell: p.prop.renderCell,
                        valueGetter: p.prop.valueGetter,
                    };
                    if (p.prop.enumStore != null || p.prop.enum != null) {
                        gridColDef.sortComparator = (a, b, c, d) => {
                            return gridStringOrNumberComparator(valueFormatter(a), valueFormatter(b), c, d);
                        }
                    }
                    return gridColDef;
                })
        };
    }, [config.columnValueTransforms, def.landCode, listProps]);

    const rowHeight = config.rowHeight ?? 40;

    const apiRef = useGridApiRef();
    useRxJsSub(() => store.onFetch.subscribe({
        next(p) {
            if (p.unintrusive !== true) {
                const position = apiRef.current.getScrollPosition();
                if (position.top !== 0) {
                    apiRef.current.scroll({ top: 0 });
                }
            }
        }
    }));

    useRxJsSub(() => (config.scrollToSelectedItem$ || of<void>())
        .subscribe({
            next() {
                const store = useStore.getState();
                const index = store.selectedItemId == null ? null : store.filteredSortedItems.findIndex(p => getId(p, store) === store.selectedItemId);
                if (index != null && index >= 0) {
                    const top = index * rowHeight;
                    setTimeout(() => {
                        apiRef.current.scroll({ top });
                    }, 200);
                }
            }
        }));

    const { itemColor, theme } = useConfig(p => p.clientData);
    const itemsPerPage = (store.config.itemsPerPageMin ?? store.config.itemsPerPageMax) == null ? (store.itemsPerPage ?? defaultItemsPerPage) :
        Math.min(store.config.itemsPerPageMax ?? 100, Math.max(store.config.itemsPerPageMin ?? 1, store.filteredSortedItems.length));

    // this is here to fix bug with horizontal scrollbar not hiding when it should, when zoom in browser is 67%.
    const [hideScrollbar, setHideScrollbar] = useState(false);
    const hideScrollbarTimeout = useRef<number | null>(null);
    const height = (itemsPerPage + 1) * rowHeight + (config.header ? 41 : 0) + 3 + (config.hideFooter ? 0 : 52) + (config.assumeHavingHorizontalScrollbar ? 18 : 0);

    return <DataGrid
        apiRef={apiRef}
        rowsLoadingMode={store.config.supportsApiFilterSortPage ? "server" : undefined}
        disableColumnFilter
        disableColumnSelector
        rows={store.filteredSortedItems as GridValidRowModel[]}
        columns={columns}
        sortingMode={store.config.supportsApiFilterSortPage ? "server" : "client"}
        sortModel={store.sortModel}
        onSortModelChange={config.readOnly ? () => { } : store.setSortModel}
        autoHeight={ifNull(config.autoHeight, false)}
        style={config.style}
        rowBuffer={10}
        localeText={{
            noRowsLabel: getResource("Ui.DataGrid.NoRows", "No rows") as string,
        }}
        onResize={p => {
            setHideScrollbar(true);
            if (hideScrollbarTimeout.current != null) {
                window.clearTimeout(hideScrollbarTimeout.current);
            }
            hideScrollbarTimeout.current = window.setTimeout(() => setHideScrollbar(false), 100);
        }}
        sx={{
            '& .Mui-even:not(:hover):not(.Mui-selected)': { backgroundColor: '#f8f8f8' },
            "& .MuiDataGrid-row:hover:not(.Mui-selected)": {
                backgroundColor: `${itemColor.hover} !important`,
            },
            "& .MuiDataGrid-row.Mui-selected:not(:hover)": {
                backgroundColor: `${itemColor.selected} !important`,
            },
            "& .MuiDataGrid-row.Mui-selected:hover": {
                backgroundColor: `${itemColor.hoverAndSelected} !important`,
            },
            '& .bold': {
                fontWeight: "bold",
            },
            '& .greenRibbon': {
                "::before": {
                    content: "''",
                    borderLeft: `5px solid ${theme.palette.positive.main}`,
                    position: "absolute",
                    height: `${rowHeight}px`,
                },
            },
            '& .redRibbon': {
                "::before": {
                    content: "''",
                    borderLeft: `5px solid ${theme.palette.negative.main}`,
                    position: "absolute",
                    height: `${rowHeight}px`,
                },
            },
            '& .warningRibbon': {
                "::before": {
                    content: "''",
                    borderLeft: `5px solid ${theme.palette.warning.main}`,
                    position: "absolute",
                    height: `${rowHeight}px`,
                },
            },
            '& .strikeThrough': {
                textDecoration: "line-through",
            },
            height,
            ...hideScrollbar ? { '& .MuiDataGrid-virtualScroller': { overflowX: "hidden" } } : {},
        }}
        headerHeight={rowHeight}
        rowHeight={rowHeight}
        components={{
            Header: config.header,
        }}
        loading={(!store.isUnintrusiveFetch && store.fetching) || listProps.some(p => p.fetching)}
        onSelectionModelChange={config.readOnly ? () => { } : p => {
            if (p[0] == null) {
                store.setSelectedItem(null);
            } else {
                store.setSelectedItem(store.items.find(r => getId(r, store) === p[0]) || null);
            }
            if (config.onRowSelected != null) {
                config.onRowSelected(p[0]);
            }
        }}
        selectionModel={store.selectedItem != null ? [getId(store.selectedItem, store)] : []}
        onRowDoubleClick={row => {
            if (config.onDoubleClick && row.row != null) {
                config.onDoubleClick(row.row);
                clearSelection();
            }
        }}
        onCellClick={row => {
            if (config.onClick && row.row != null) {
                config.onClick(row.row);
            }
        }}
        getRowId={store.config.getId as GridRowIdGetter<GridValidRowModel>}
        getRowClassName={(params) => [
            ...(config.getRowClass?.(params.row as T) || []),
            params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd',
        ].filter(p => !!p).join(" ")}
        hideFooter={config.hideFooter}
    />;
}

function clearSelection() {
    window.getSelection()?.removeAllRanges();
}

export function renderListStore<T>(useStore: UseListStore<T>, config: ListStoreGridConfig<T> = {}) {
    return <StoreWrapper useStore={() => {
        const store = useStore(p => ({ listPropsId: p.listPropsId, listProps: p.listProps }));
        const userTypeId = useAccountStore(p => p.data?.accessTokenDecoded.userTypeId);
        const enums = useMemo(() => store.listProps.map(p => p.enumStore?.getState().items$!).filter(p => p != null), [store.listProps]);
        const enumsId = useRxJsObservablesWatcher(enums);
        const listProps = useMemo(() => getListProps(store, userTypeId, enumsId), [store, userTypeId, enumsId]);
        return {
            store,
            listProps,
        };
    }} render={data => <>
        {!config.readOnly && <ListStoreFilters key={"filters" + data.store.listPropsId} useStore={useStore} config={config} listProps={data.listProps} />}
        <ListStoreGrid key={"grid" + data.store.listPropsId} useStore={useStore} config={config} listProps={data.listProps} />
    </>} />
}

export function ListStoreDropDown<T>(props: {
    useStore: UseListStore<T>;
    label: string;
    getItemId: (item: T) => string | number;
    getItemName: (item: T) => string;
    disabled?: boolean;
    readOnly?: boolean;
}) {
    const store = props.useStore(p => ({ selectedItem: p.selectedItem, items: p.filteredSortedItems }));
    return <Autocomplete
        disabled={props.disabled} readOnly={props.readOnly}
        options={store.items}
        value={store.selectedItem || null}
        getOptionLabel={p => props.getItemName(p)}
        onChange={(p, v) => {
            props.useStore.getState().setSelectedItem(v);
        }}
        renderInput={(params) => <TextField {...params}
            label={props.label} />}
    />;
}

export function listHeaderItems(items: (null | false | [string, () => JSX.Element])[], itemsRight: (null | false | [string, () => JSX.Element])[] = []) {
    var result = listHeaderItemsDirect(items, itemsRight);
    return result == null ? undefined : () => result!;
}

export function listHeaderItemsDirect(items: (null | false | [string, () => JSX.Element])[], itemsRight: (null | false | [string, () => JSX.Element])[] = []) {
    const definedItems = (items as [string, () => JSX.Element][]).filter(p => p);
    const definedItemsRight = (itemsRight as [string, () => JSX.Element][]).filter(p => p);
    return definedItems.length === 0 && definedItemsRight.length === 0 ? undefined : <Grid container flexWrap="nowrap">
        <Grid item container pt={2} pl={2} pr={2} spacing={2} sx={{ flexGrow: 1 }}>{definedItems.map(p => <Grid item key={p[0]}>{p[1]()}</Grid>)}</Grid>
        <Grid item container xs pt={2} pl={2} pr={2} spacing={2} flexWrap="nowrap">{definedItemsRight.map(p => <Grid item key={p[0]}>{p[1]()}</Grid>)}</Grid>
    </Grid>;
}

function getListProps<T>(store: Pick<ListStore<T>, "listProps">, userTypeId: UserTypeId | null | undefined, enumsId: number) {
    return store.listProps.map(p => {
        const enumStore = p.enumStore ? p.enumStore.getState() : null;
        const fetching = enumStore ? enumStore.fetching : false;
        const readOnlyValue = p.readOnlyValue ? p.readOnlyValue({ userTypeId }) : null;
        let enumItems = enumStore ? enumStore.items : p.enum ? p.enum : null;
        if (enumItems && readOnlyValue != null && !enumItems.some(p => p.id === readOnlyValue)) {
            enumItems = enumItems.concat({ id: readOnlyValue as string, code: readOnlyValue, name: "" } as EnumItemDto);
        }
        return { prop: p, enumItems, fetching, readOnlyValue };
    });
}

export function ListStoreFilters<T>(props: { useStore: UseListStore<T>; config: ListStoreGridConfig<T>; listProps: ReturnType<typeof getListProps<T>>; }) {
    const store = props.useStore(p => ({
        listProps: p.listProps,
        fetch: p.fetch,
        resetFilterData: p.resetFilterData,
    }));

    function clear() {
        store.resetFilterData();
        if (props.config.clearExternalFilters) {
            props.config.clearExternalFilters();
        }
        if (props.config.instantSearch) {
            store.fetch({ updateFetchFilterData: true });
        }
        if (props.config.onClear) {
            props.config.onClear();
        }
    }

    const filters = props.listProps.filter(p => p.prop.filter != null);

    return <>
        {(filters.length > 0 || props.config.renderExternalFilters != null) && <Grid container maxWidth={props.config.filtersMaxWidth}>
            <Grid item xs={12} container columnSpacing={2}
                onKeyDown={props.config.instantSearch ? undefined : p => { if (p.key === "Enter") { store.fetch({ updateFetchFilterData: true }); } }}>
                {props.config.renderExternalFilters?.()}
                {filters.map(p => <Filter key={getListPropFilterProps(p.prop)[0] as string} item={p}
                    useStore={props.useStore} config={props.config} />)}
                {!props.config.instantSearch && <SearchButton onClick={() => store.fetch({ updateFetchFilterData: true })} />}
                <ClearButton onClick={clear} />
            </Grid>
        </Grid>}
    </>;
}

function Filter<T>({ item, useStore, config }: {
    item: ArrayElement<ReturnType<typeof getListProps<T>>>;
    useStore: UseListStore<T>;
    config: ListStoreGridConfig<T>;
}) {
    const store = useStore(p => ({
        dataFilters: p.dataFilters,
        fetch: p.fetch,
        setFilterData: p.setFilterData,
    }));

    const width = item.prop.filterWidth || 1;
    const ref = useRef<HTMLInputElement>();

    useEffect(() => {
        if (item.prop.autoFocus) {
            ref.current?.focus();
        }
    }, [ref, item.prop.autoFocus]);

    function getListPropFilterValue(item: ListProp<T>, getDefault: () => string | number | null) {
        const filter = store.dataFilters[getListPropFilterName(item)];
        return filter ? filter.value : getDefault();
    }

    function getListPropFilterValueFormatted(item: ListProp<T>) {
        const filter = store.dataFilters[getListPropFilterName(item)];
        return filter ? filter.valueFormatted : undefined;
    }

    function setPropFilterValue(
        item: ListProp<T>,
        value: string | number | boolean | null | (string | number | boolean | null)[],
        valueFormatted?: string | null | (string | null | undefined)[]) {
        const filterName = getListPropFilterName(item);
        const filter = store.dataFilters[filterName];
        if (value != null || valueFormatted != null) {
            store.setFilterData(filterName, {
                props: getListPropFilterProps(item),
                value,
                valueFormatted,
                operator: item.operator || (item.enumStore ? "Equal" : "Like"),
                fullText: item.filterFullText,
            });
        } else if (filter != null) {
            store.setFilterData(filterName, null);
        }
        if (config.instantSearch) {
            store.fetch({ updateFetchFilterData: true });
        }
    }

    function getArrayFilterValue<T>(index: number, filterValue: T | T[]) {
        if (Array.isArray(filterValue)) {
            return filterValue[index];
        } else {
            return null;
        }
    }

    function setArrayFilterValue<T>(index: number, filterValue: T | T[], value: T) {
        const newFilterValue = Array.isArray(filterValue) ? [...filterValue] : [];
        newFilterValue[index] = value;
        if (newFilterValue.some(p => p != null)) {
            return newFilterValue;
        } else {
            return null;
        }
    }

    if (item.prop.type === "date" && item.prop.operator === "Between") {
        const renderRangePart = (label: string, index: number) => {
            return <Grid item xs={6 * width} sm={3 * width} lg={1 * width}>
                <DateField
                    inputRef={p => ref.current = p ?? undefined}
                    disabled={item.readOnlyValue != null}
                    label={item.prop.label + " " + label}
                    setValue={(date, dateFormatted) => setPropFilterValue(item.prop,
                        setArrayFilterValue(index, getListPropFilterValue(item.prop, () => null), date),
                        setArrayFilterValue(index, getListPropFilterValueFormatted(item.prop), dateFormatted))}
                    value={item.readOnlyValue != null ?
                        item.readOnlyValue as string :
                        getArrayFilterValue(index, getListPropFilterValue(item.prop, () => "")) as string}
                    valueFormatted={getArrayFilterValue(index, getListPropFilterValueFormatted(item.prop))}
                />
            </Grid>;
        }
        return <>
            {renderRangePart("from", 0)}
            {renderRangePart("to", 1)}
        </>;
    } else if (item.enumItems) {
        return <Grid item xs={6 * width} sm={3 * width} lg={1 * width}>
            <FormControl disabled={item.readOnlyValue != null}>
                <InputLabel>{item.prop.label}</InputLabel>
                <Select
                    inputRef={p => ref.current = p ?? undefined}
                    label={item.prop.label}
                    value={item.readOnlyValue != null ? item.readOnlyValue : getListPropFilterValue(item.prop, () => enumAllValue)}
                    onChange={e => setPropFilterValue(item.prop, (e.target.value == null || e.target.value === enumAllValue) ? null : e.target.value)}
                >
                    <MenuItem value={enumAllValue}>All</MenuItem>
                    {item.enumItems.map(p =>
                        <MenuItem value={p.id} key={p.id}>{p.name}</MenuItem>)}
                </Select>
            </FormControl>
        </Grid>;
    } else {
        return <Grid item flexGrow={item.prop.filterWidthFlexGrow}
            xs={item.prop.filterWidthFlexGrow != null ? undefined : 6 * width}
            sm={item.prop.filterWidthFlexGrow != null ? undefined : 3 * width}
            lg={item.prop.filterWidthFlexGrow != null ? undefined : 1 * width}>
            <TextField
                inputRef={p => ref.current = p ?? undefined}
                disabled={item.readOnlyValue != null}
                label={item.prop.label}
                onChange={(event) => setPropFilterValue(item.prop, event.currentTarget.value || null)}
                value={item.readOnlyValue != null ? item.readOnlyValue : getListPropFilterValue(item.prop, () => "")}
            />
        </Grid>;
    }
}

export function SearchButton(props: { onClick: () => void; disabled?: boolean; }) {
    return <Grid item xs="auto"><Button title={getResource("Ui.Search", "Search") as string} variant="contained" disabled={props.disabled} onClick={props.onClick} sx={InputSizedButtonSx} color="positive"><Search /></Button></Grid>;
}

export function ClearButton(props: { onClick: () => void; disabled?: boolean; }) {
    return <Grid item xs="auto"><Button title={getResource("Ui.Clear", "Clear") as string} variant="contained" disabled={props.disabled} onClick={props.onClick} sx={InputSizedButtonSx}><Clear /></Button></Grid>;
}

export function useScrollToSelectedItemSubject() {
    const [subject] = useState(() => new Subject<void>())
    return subject;
}

export function useScrollToSelectedItemOnce<T>(useListStore: UseListStore<T>) {
    const subject = useScrollToSelectedItemSubject();
    useEffect(() => {
        const sub = useListStore.getState().items$.pipe(observeOn(asapScheduler)).subscribe({
            next(value) {
                if (value.items.length > 0) {
                    sub.unsubscribe();
                    subject.next();
                }
            },
        });
        return () => sub.unsubscribe();
    }, [useListStore, subject]);
    return subject.asObservable();
}
